import { Component, input, model, output, effect, signal, ViewChild, inject, LOCALE_ID, DestroyRef, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { CommonModule, registerLocaleData, DatePipe } from '@angular/common';
import { FormGroup, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { FormMode } from 'app/core/constants/form-mode';
import { VehicleThreeDComponent } from '../../../management-vehicle/vehicle-three-d/vehicle-three-d.component';
import { formatVehiclePartLabel, formatVehiclePartList } from '../../../management-vehicle/vehicle-damage-selector/vehicle-damage-parts';
import { CatalogService } from '@services/data/catalog.service';
import localeEs from '@angular/common/locales/es';
import Swal from 'sweetalert2';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Global } from '@services/core/Global';
import { ClaimService } from '../../services/claim.service';
import { areAllDetailsFinalized, getDetailEvidenceProgress, sanitizeNumericValue } from '../../utils/claim-details.util';
import { AuthService } from '@services/core/auth.service';
import { RequestService } from '../../../management-request/services/requests.service';

import { ViewerComponent, DocViewerItem } from 'app/feature/management-document/components/viewer/viewer.component';
import { ClaimDetailFormComponent } from '../claim-detail-form/claim-detail-form.component';
import { ClaimDetailDocumentComponent } from '../claim-detail-document/claim-detail-document.component';
import { ClaimDetailListComponent } from '../claim-detail-list/claim-detail-list.component';
import { ClaimSummaryComponent } from '../claim-summary/claim-summary.component';
import { StorageService } from '@services/core/storage.service';
import { AppSettings } from '@services/settings/app-settings.service';
import {
  canAddDetail,
  fmrAmount,
  noticeMaxDays,
  reportMaxDays as catalogReportMaxDays,
  minorMaxPct,
  daysSince
} from '../../utils/claim-process-rules.util';

registerLocaleData(localeEs);

@Component({
  selector: 'app-claim-details-manager',
  templateUrl: './claim-details-manager.component.html',
  styleUrls: ['./claim-details-manager.component.css'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, DatePipe, ViewerComponent, ClaimDetailFormComponent, ClaimDetailDocumentComponent, ClaimDetailListComponent, ClaimSummaryComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ClaimService, { provide: LOCALE_ID, useValue: 'es-ES' }]
})
export class ClaimDetailsManagerComponent implements OnInit {
  claimForm = input<FormGroup>();
  claimData = input<any>();
  partyData = input<any>();
  mode = model<FormMode>(FormMode.VIEW);
  vehicleData = input<any[]>();
  claimTypes = input<any[]>();
  selectedVehicleType = model<string>('sedan');
  initialAffectedParts = input<string[]>([]);
  claimDetails = model<any[]>([]);
  canEditDetails = input<boolean>(false);
  canReplaceObserved = input<boolean>(false);
  /** Aprobación final de evidencias (requiredRole BPM). */
  canFinalApproveDocs = input<boolean>(false);
  /** Editar negligencia/deducible del claim: BPM (rol requerido / revisor final). */
  canEditNegligence = input<boolean>(false);
  isPublicPreview = input<boolean>(false);
  layout = input<'full' | 'preview'>('full');

  public http = inject(HttpClient);
  private storageService = inject(StorageService);
  public appSettings = inject(AppSettings);



  get isStatusEditable(): boolean {
    const editableStatuses = ['MINISTRY_APPROVED', 'REVIEWING'];
    return editableStatuses.includes(this.localClaimData()?.statusSlug);
  }

  get isTotalLoss(): boolean {
    return this.claimForm()?.get('claimType')?.value === 'TOTAL_LOSS' || this.localClaimData()?.claimType === 'TOTAL_LOSS';
  }

  get currentVehicleValue(): number {
    const claimType = this.claimForm()?.get('claimType')?.value || this.localClaimData()?.claimType;
    if (claimType === 'ACCIDENT_THIRD_PARTY') return 0;
    
    const vehicleId = this.claimForm()?.get('vehicleId')?.value;
    const vehicle = this.vehicleData()?.find(x => x.uuid === vehicleId || x.id === vehicleId);
    // En la bandeja de revisión no existe claimForm ni vehicleData, solo el vehículo persistido del siniestro
    return Number(vehicle?.commercialValue ?? this.localClaimData()?.vehicle?.commercialValue ?? 0);
  }

  get totalDeductible(): number {
    const fromDetails = this.claimDetails().reduce((acc, d) => acc + Number(d.deductible ?? 0), 0);
    if (fromDetails > 0) return fromDetails;
    const claimDed = Number(this.localClaimData()?.deductible ?? 0);
    if (claimDed > 0) return claimDed;
    if (this.isTotalLoss) {
      return this.currentVehicleValue * (this.totalLossRetentionPct / 100);
    }
    return 0;
  }

  get totalLossRetentionPct(): number {
    return Number(this.deductiblePercentages().totalLossPercentage ?? 15);
  }

  get isFmrLimitReached(): boolean {
    return !this.canRegisterAnotherDetail().ok;
  }

  /** ¿Se puede registrar otro daño según reglas del proceso? */
  canRegisterAnotherDetail(): { ok: boolean; reason?: string } {
    const processSlug = this.claimForm()?.get('claimType')?.value
      || this.localClaimData()?.claimType
      || null;
    return canAddDetail({
      catalog: this.catalogService,
      processSlug,
      details: this.claimDetails(),
      isTotalLoss: this.isTotalLoss,
      partyFmr: this.partyData()?.fmr
    });
  }

  get laborCost(): number {
    return this.claimForm()?.get('laborCost')?.value ?? this.localClaimData()?.laborCost ?? 0;
  }

  get partsCost(): number {
    return this.claimForm()?.get('partsCost')?.value ?? this.localClaimData()?.partsCost ?? 0;
  }

  get totalCost(): number {
    if (this.isTotalLoss) {
      return this.claimDetails().length > 0 ? this.currentVehicleValue : 0;
    }
    return this.claimForm()?.get('totalCost')?.value ?? this.localClaimData()?.totalCost ?? 0;
  }

  /** Monto neto que reembolsa el FPV (bruto − deducible). El socio costea el deducible. */
  get netReimbursement(): number {
    return Math.max(0, this.totalCost - this.totalDeductible);
  }

  partsSelected = output<string[]>();
  vehicleChange = output<any>();
  claimTypeChange = output<string>();
  documentUploaded = output<void>();
  headerEditorActionChange = output<'save' | 'finalize' | null>();

  formMode = FormMode;
  isDriverSameAsParty = signal<boolean>(true);

  currentDetail = signal<any>({
    description: '', laborCost: 0, partsCost: 0, affectedParts: '[]',
    dateOfLoss: '', driverName: '', driverLicense: '', driverLicenseClass: '',
    statusSlug: 'PENDING', isNegligence: false, deductible: 0
  });
  detailFile: File | null = null;
  detailFileName: string | null = null;




  @ViewChild(VehicleThreeDComponent) vehicleThreeDComponent!: VehicleThreeDComponent;
  @ViewChild('detailViewModal') detailViewModalTemplate!: any;
  @ViewChild(ClaimDetailDocumentComponent) detailDocumentComp?: ClaimDetailDocumentComponent;

  private catalogService = inject(CatalogService);
  private destroyRef = inject(DestroyRef);
  private modalService = inject(NgbModal);
  private claimService = inject(ClaimService);
  private requestService = inject(RequestService);
  private authService = inject(AuthService);
  private _subs: any[] = [];

  public localClaimData = signal<any>(null);

  selectedDetailView = model<any>(null);
  fmrRuleValue = signal<number>(1285000);
  deductiblePercentages = signal<{ normalPercentage: number, negligentPercentage: number, totalLossPercentage?: number }>({ normalPercentage: 10, negligentPercentage: 30, totalLossPercentage: 15 });
  isSavingDetail = signal<boolean>(false);
  isSavingNewDetail = signal<boolean>(false);
  isEditingDetail = model<boolean>(false);
  isViewerOpen = false;
  viewerItems: DocViewerItem[] = [];
  activeViewerId: string | number = 0;
  reportMaxDays = signal<number>(60);
  
  openDetailView(detail: any, opts?: { skipTimeframeCheck?: boolean }) {
    this.selectedDetailView.set(detail);
    this.isEditingDetail.set(true);
    if (!opts?.skipTimeframeCheck) this._checkReportTimeframe(detail.dateOfLoss);
  }

  private _checkReportTimeframe(dateStr: string) {
    const diffDays = daysSince(dateStr);
    if (diffDays == null) return;
    const processSlug = this.claimForm()?.get('claimType')?.value
      || this.localClaimData()?.claimType
      || null;
    const maxDays = noticeMaxDays(this.catalogService, processSlug);
    if (diffDays > maxDays) {
      Swal.fire({
        icon: 'warning',
        title: 'Plazo de aviso superado',
        text: `Han transcurrido ${diffDays} días desde la ocurrencia (límite de aviso: ${maxDays} días, Art. VII.4). La entrega de respaldos pasados este plazo está sujeta a revisión especial.`,
        confirmButtonText: 'Entendido'
      });
    }
  }

  private _openViewerOverlay(doc: any) {
    const mimeType = doc.mimeType ?? null;
    const fileName = doc.fileName ?? null;
    const isPdf = String(mimeType || '').includes('pdf') || /\.pdf$/i.test(String(fileName || ''));
    this.viewerItems = [{
      id: doc.uuid || 'temp-doc-viewer-id',
      imageUrl: Global.url + doc.filePath,
      title: doc.name || 'Evidencia',
      status: 'success',
      mimeType,
      fileName,
      kind: isPdf ? 'pdf' : 'image'
    }];
    this.activeViewerId = doc.uuid || 'temp-doc-viewer-id';
    this.isViewerOpen = true;
  }

  triggerModalFileUpload(uuid: string) {
    document.getElementById('file-upload-' + uuid)?.click();
  }

  onModalFileSelected(event: any, doc: any) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    this.claimService.uploadClaimDocument(doc.uuid, file).subscribe({
      next: () => {
        this.Toast.fire({ icon: 'success', title: 'Archivo subido correctamente' });
        this.documentUploaded.emit();
      },
      error: (err) => {
        this.Toast.fire({ icon: 'error', title: err.error?.message ?? 'No se pudo subir el archivo.' });
      }
    });
  }

  getActiveViewerDoc(): any {
    const docs = this.detailDocumentComp?.selectedDetailDocs()
      ?? this.detailDocumentComp?.buildDocsList(this.selectedDetailView())
      ?? [];
    return docs.find((d: any) => d.uuid === this.activeViewerId) ?? null;
  }

  updateDocumentStatus(doc: any, statusSlug: string, callback?: () => void) {
    if (!doc?.uuid) return;
    const params = { id: doc.uuid, statusSlug, origin: 'CLAIM' };
    this.requestService.updateDocument(params).subscribe(() => {
      doc.statusSlug = statusSlug;
      this.documentUploaded.emit();
      callback?.();
    });
  }

  approveViewerDoc() {
    this.updateDocumentStatus(this.getActiveViewerDoc(), 'APPROVED');
  }

  rejectViewerDoc() {
    this.updateDocumentStatus(this.getActiveViewerDoc(), 'REJECTED');
  }

  // Retorna la clase CSS del badge de estado
  getStatusBadgeClass(status: string): string {
    if (status === 'APPROVED') return 'bg-success text-white';
    if (status === 'REJECTED') return 'bg-danger text-white';
    return 'bg-warning text-dark';
  }

  public Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 2000,
    timerProgressBar: true,
  });

  ngOnInit(): void {
    const form = this.claimForm();
    const party = this.partyData();
    if (form && party && form.get('driverLicense')?.value !== party.rut) {
      this.isDriverSameAsParty.set(false);
    }
    this._loadFmrRule();
    const vehicleId = form?.get('vehicleId')?.value;
    if (vehicleId) this.loadDeductiblePercentages(vehicleId);
  }

  private _loadFmrRule() {
    // FMR y plazos vienen del catálogo (ruleMap del proceso); sin RuleService suelto.
    const processSlug = this.claimForm()?.get('claimType')?.value
      || this.localClaimData()?.claimType
      || null;
    this.fmrRuleValue.set(fmrAmount(this.catalogService, this.partyData()?.employeeAps?.fmr ?? this.partyData()?.fmr));
    this.reportMaxDays.set(catalogReportMaxDays(this.catalogService, processSlug));
  }

  constructor() {
    this.destroyRef.onDestroy(() => this._clearSubscriptions());

    effect(() => {
      const claim = this.claimData();
      this.localClaimData.set(claim || null);
    });

    effect(() => {
      const claim = this.localClaimData();
      if (!claim || !this.isSavingNewDetail()) return;
      const selected = this.selectedDetailView();
      const details = claim.details ?? [];
      const found = details.find((d: any) =>
        (selected?.uuid && d.uuid === selected.uuid)
        || (selected?.description && d.description === selected.description)
      ) || details[details.length - 1];
      if (found?.id || found?.uuid) {
        this.isSavingNewDetail.set(false);
        // Mantener el editor abierto y mostrar evidencias sin cerrar/reabrir
        this.openDetailView(found, { skipTimeframeCheck: true });
        this.Toast.fire({
          icon: 'success',
          title: 'Daño guardado. Ahora cargue las evidencias requeridas.'
        });
        queueMicrotask(() => this.documentUploaded.emit());
      }
    });

    // Configura autocompletado reactivo de conductor
    effect(() => {
      const form = this.claimForm();
      const party = this.partyData();
      const isSame = this.isDriverSameAsParty();
      if (form && party) {
        this.syncDriverDetails(form, party, isSame);
      }
    });

    // Configura suscripciones de cambio de formulario interactivo
    effect(() => {
      const form = this.claimForm();
      if (form) {
        this._registerFormSubscriptions(form);
      }
    });

    // Sincroniza tipo de vehículo en modo VIEW a partir de claimData
    effect(() => {
      const data = this.localClaimData();
      if (data) {
        this._updateVehicleTypeFromData(data);
      }
    });

    // Sincroniza la señal claimDetails con los detalles del siniestro
    effect(() => {
      const data = this.localClaimData();
      this.claimDetails.set(data?.details || []);
    }, { allowSignalWrites: true });

    effect(() => {
      const action = this.headerEditorAction();
      queueMicrotask(() => this.headerEditorActionChange.emit(action));
    });
  }

  headerEditorAction(): 'save' | 'finalize' | null {
    if (!this.isEditingDetail()) return null;
    if (this.mode() === FormMode.VIEW) return null;
    const det = this.selectedDetailView();
    if (det?.statusSlug === 'REVIEWING') return null;
    if ((det?.id || det?.uuid) && this.canCloseDetail(det)) return 'finalize';
    return 'save';
  }

  runHeaderEditorAction(): void {
    const det = this.selectedDetailView();
    if (this.headerEditorAction() === 'finalize') {
      this.closeDetail(det);
      return;
    }
    if (!det?.id && !det?.uuid) {
      this.addDetail();
      return;
    }
    this.saveModalDetailChanges(det);
  }

  // Sincroniza datos del conductor con el socio o habilita inputs
  syncDriverDetails(form: FormGroup, party: any, isSame: boolean) {
    const name = form.get('driverName');
    const license = form.get('driverLicense');
    if (isSame) {
      name?.setValue(`${party.names ?? ''} ${party.lastNames ?? ''}`.trim());
      license?.setValue(party.rut);
      form.get('driverLicenseClass')?.setValue(null);
    }
    const list = [name, license, form.get('driverLicenseClass')];
    this._setControlsState(!isSame, list);
  }

  private _setControlsState(enable: boolean, ctrls: any[]) {
    ctrls.forEach(c => {
      enable ? (c?.enable({ emitEvent: false }), c?.setValidators([Validators.required])) : (c?.disable({ emitEvent: false }), c?.clearValidators());
      c?.updateValueAndValidity({ emitEvent: false });
    });
  }

  /** Tope por línea menor (Art. VII.7): % FMR del proceso. */
  getLimitFmr(): number {
    const processSlug = this.claimForm()?.get('claimType')?.value
      || this.localClaimData()?.claimType
      || null;
    const fmr = fmrAmount(this.catalogService, this.partyData()?.employeeAps?.fmr ?? this.partyData()?.fmr ?? this.fmrRuleValue());
    const pct = minorMaxPct(this.catalogService, processSlug);
    return fmr * (pct / 100);
  }

  get claimIsNegligence(): boolean | null {
    const details = this.claimDetails();
    if (details?.length) {
      if (details.some((d) => d.isNegligence === true)) return true;
      if (details.every((d) => d.isNegligence === false || d.isNegligence == null)) return false;
    }
    const v = this.localClaimData()?.isNegligence;
    if (v === true || v === false) return v;
    return null;
  }

  get claimProcessSlug(): string | null {
    return this.claimForm()?.get('claimType')?.value
      || this.localClaimData()?.claimType
      || null;
  }

  get canEditClaimNegligence(): boolean {
    return this.canEditNegligence() || this.canFinalApproveDocs();
  }

  // Capturar archivo del presupuesto del daño
  onFileSelected(event: any) {
    const file = event.target.files?.[0];
    if (file) {
      this.detailFile = file;
      this.detailFileName = file.name;
    }
  }

  // Cierra el editor del detalle del siniestro
  closeDetailEditor() {
    this.isEditingDetail.set(false);
    this.selectedDetailView.set(null);
    this.isSavingDetail.set(false);
  }

  openCreateDetail() {
    const gate = this.canRegisterAnotherDetail();
    if (!gate.ok) {
      this.Toast.fire({ icon: 'warning', title: gate.reason || 'No se pueden agregar más daños.' });
      return;
    }
    this._resetDetailForm();
    this.selectedDetailView.set(this.currentDetail());
    this.isEditingDetail.set(true);
  }

  addDetail() {
    const d = this.selectedDetailView();
    if (!this._validateDetail(d)) return;
    const total = this.isTotalLoss ? this.currentVehicleValue : (sanitizeNumericValue(d.laborCost) + sanitizeNumericValue(d.partsCost));
    if (this._isFmrLimitExceeded(total)) return;
    this.isSavingNewDetail.set(true);
    this._appendNewDetail(d, total);
  }

  // Agrega el detalle a la lista acumulada
  private _appendNewDetail(d: any, total: number) {
    const item = { ...d, totalCost: total, file: this.detailFile, fileName: this.detailFileName };
    const newList = [...this.claimDetails(), item];
    this.claimDetails.set(newList);
    this.updateClaimFormTotals(newList);
  }

  private _validateDetail(d: any): boolean {
    const description = String(d.description ?? '').trim();
    const damageDescription = String(d.damageDescription ?? '').trim();
    if (!description) {
      this.Toast.fire({ icon: 'warning', title: 'Ingrese la descripción del daño.' });
      return false;
    }
    if (description.length < 30) {
      this.Toast.fire({ icon: 'warning', title: 'La reseña del accidente debe tener al menos 30 caracteres.' });
      return false;
    }
    if (description.length > 1000) {
      this.Toast.fire({ icon: 'warning', title: 'La reseña del accidente no puede superar 1000 caracteres.' });
      return false;
    }
    if (!damageDescription) {
      this.Toast.fire({ icon: 'warning', title: 'Ingrese la descripción de los daños físicos.' });
      return false;
    }
    if (damageDescription.length < 20) {
      this.Toast.fire({ icon: 'warning', title: 'La descripción de daños debe tener al menos 20 caracteres.' });
      return false;
    }
    if (damageDescription.length > 500) {
      this.Toast.fire({ icon: 'warning', title: 'La descripción de daños no puede superar 500 caracteres.' });
      return false;
    }
    if (!this.isTotalLoss && (sanitizeNumericValue(d.laborCost) + sanitizeNumericValue(d.partsCost)) <= 0) {
      this.Toast.fire({ icon: 'warning', title: 'El costo del daño debe ser mayor a $0.' });
      return false;
    }
    return this._validateDetailDriverAndDate(d);
  }

  private _validateDetailDriverAndDate(d: any): boolean {
    if (!d.dateOfLoss) {
      this.Toast.fire({ icon: 'warning', title: 'Seleccione la fecha del siniestro.' });
      return false;
    }
    const isSame = !d.driverLicense || d.driverLicense === this.partyData()?.rut;
    const hasInvalidDriver = !isSame && (!d.driverName || !d.driverLicense || !d.driverLicenseClass);
    if (hasInvalidDriver) {
      this.Toast.fire({ icon: 'warning', title: 'Complete los datos del conductor.' });
      return false;
    }
    return true;
  }

  /**
   * Art. VII.7: el 25% FMR clasifica menor vs mayor; no bloquea el alta.
   * Los topes de cantidad se validan en canAddDetail / openCreateDetail.
   */
  private _isFmrLimitExceeded(_total: number): boolean {
    const gate = this.canRegisterAnotherDetail();
    if (!gate.ok) {
      Swal.fire({
        icon: 'error',
        title: 'No se puede agregar el daño',
        text: gate.reason || 'Se alcanzó el límite de daños del proceso.'
      });
      return true;
    }
    return false;
  }

  private _resetDetailForm() {
    const preset = this.initialAffectedParts() ?? [];
    this.currentDetail.set({
      description: '', laborCost: 0, partsCost: 0,
      affectedParts: preset.length ? [...preset] : [],
      dateOfLoss: '', driverName: '', driverLicense: '', driverLicenseClass: '',
      statusSlug: 'PENDING', isNegligence: false, deductible: 0
    });
    this.detailFile = null;
    this.detailFileName = null;
  }

  getModalFormMode(det: any): FormMode {
    // Si el daño individual ya está finalizado/en revisión, debe ser de solo lectura
    if (det?.statusSlug === 'REVIEWING') return FormMode.VIEW;

    // Si estamos en la pantalla de crear siniestro (add-claim)
    if (this.mode() !== FormMode.VIEW) return FormMode.EDIT;
    
    // Si estamos en la bandeja de solicitudes, solo se puede editar el detalle 
    // si el usuario tiene permisos de aprobación Y el estado permite edición (ej. luego de que el ministro lo apruebe)
    const editableStatuses = ['MINISTRY_APPROVED', 'REVIEWING'];
    const claimStatus = this.localClaimData()?.statusSlug;
    if (this.canEditDetails() && editableStatuses.includes(claimStatus)) {
      return FormMode.EDIT;
    }
    
    return FormMode.VIEW;
  }

  saveModalDetailChanges(det: any) {
    if (!this._validateDetail(det)) return;
    if (this.localClaimData()?.uuid) {
      if (det.id || det.uuid) {
        this._updateDetailOnServer(det);
      } else {
        this._createDetailOnServer(det);
      }
    } else {
      this._saveDetailToLocal(det);
    }
  }

  private _saveDetailToLocal(det: any) {
    const list = this.claimDetails();
    const idx = list.findIndex(d => 
      (d.id && det.id && d.id === det.id) || 
      (d.uuid && det.uuid && d.uuid === det.uuid) || 
      (d.description === det.description)
    );
    if (idx !== -1) {
      list[idx] = { ...det, totalCost: this.isTotalLoss ? this.currentVehicleValue : (Number(det.laborCost || 0) + Number(det.partsCost || 0)) };
      this.claimDetails.set([...list]);
      this.updateClaimFormTotals(list);
    }
    this.Toast.fire({ icon: 'success', title: 'Cambios guardados localmente' });
  }

  private _createDetailOnServer(det: any) {
    const payload = {
      ...det,
      claimId: this.localClaimData().id,
      claimType: this.localClaimData().claimType,
      laborCost: sanitizeNumericValue(det.laborCost),
      partsCost: sanitizeNumericValue(det.partsCost),
      totalCost: sanitizeNumericValue(det.totalCost),
      deductible: sanitizeNumericValue(det.deductible)
    };
    Swal.fire({ title: 'Guardando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    this.claimService.createClaimDetail(payload).subscribe({
      next: () => this._handleSaveServerSuccess(),
      error: (err) => this._handleSaveServerError(err)
    });
  }

  private _updateDetailOnServer(det: any) {
    Swal.fire({ title: 'Guardando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    this.claimService.updateClaimDetail(det.uuid, {
      ...det,
      laborCost: sanitizeNumericValue(det.laborCost),
      partsCost: sanitizeNumericValue(det.partsCost),
      totalCost: sanitizeNumericValue(det.totalCost),
      deductible: sanitizeNumericValue(det.deductible),
      isNegligence: !!det.isNegligence
    }).subscribe({
      next: () => this._handleSaveServerSuccess(),
      error: (err) => this._handleSaveServerError(err)
    });
  }

  private _handleSaveServerSuccess() {
    Swal.close();
    this.Toast.fire({ icon: 'success', title: 'Guardado con éxito' });
    this.documentUploaded.emit();
  }

  private _handleSaveServerError(err: any) {
    Swal.close();
    Swal.fire('Error', err.error?.message ?? 'Error al guardar.', 'error');
  }

  private _buildServerUpdatePayload(det: any): any {
    const list = this.claimDetails().map(d => {
      const isMatch = (d.id && det.id && d.id === det.id) || (d.uuid && det.uuid && d.uuid === det.uuid);
      return isMatch ? { ...det, totalCost: this.isTotalLoss ? this.currentVehicleValue : (Number(det.laborCost || 0) + Number(det.partsCost || 0)) } : d;
    });
    return {
      ...this.claimForm()?.value,
      details: list,
      thirdPartyInfo: this.localClaimData()?.thirdPartyInfo
    };
  }

  canCloseDetail(det: any): boolean {
    return !this.closeDetailBlockedReason(det);
  }

  closeDetailBlockedReason(det: any): string {
    return this.detailDocumentComp?.closeBlockedReason()
      || ((!det?.id && !det?.uuid) ? 'Guarde el daño primero.' : '');
  }

  closeDetail(det: any) {
    const config: any = {
      title: '¿Dejar este daño en revisión?',
      text: 'El daño y sus evidencias quedarán en solo lectura. No se envía la solicitud: eso se hace después con todos los daños juntos.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, dejar en revisión'
    };
    Swal.fire(config).then(r => r.isConfirmed && this._updateDetailStatusServer(det, 'REVIEWING'));
  }

  private _updateDetailStatusServer(det: any, statusSlug: string) {
    const payload = { statusSlug, statusCode: statusSlug === 'REVIEWING' ? 2 : 1 };
    Swal.fire({ title: 'Actualizando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    this.claimService.updateClaimDetail(det.uuid, payload).subscribe({
      next: () => {
        this.selectedDetailView.set({ ...det, ...payload });
        Swal.close();
        this.Toast.fire({ icon: 'success', title: 'Estado del daño actualizado' });
        this.documentUploaded.emit();
      },
      error: (err) => {
        Swal.close();
        Swal.fire('Error', err.error?.message ?? 'Error al actualizar.', 'error');
      }
    });
  }

  getDetailStatusLabel(det: any): string {
    return this.getDetailProgress(det).label;
  }

  getDetailStatusClass(det: any): string {
    return this.getDetailProgress(det).badgeClass;
  }

  getDetailProgress(det: any): { label: string; badgeClass: string; uploaded: number; total: number } {
    return getDetailEvidenceProgress(det, this.localClaimData()?.documents);
  }

  removeDetail(index: number) {
    const detail = this.claimDetails()[index];
    if (this.localClaimData()?.uuid && (detail.id || detail.uuid)) {
      this._deleteDetailFromServer(detail);
    } else {
      const currentList = this.claimDetails().filter((_, i) => i !== index);
      this.claimDetails.set(currentList);
      this.updateClaimFormTotals(currentList);
    }
  }

  private _deleteDetailFromServer(detail: any) {
    Swal.fire({ title: 'Eliminando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    this.claimService.deleteClaimDetail(detail.uuid).subscribe({
      next: () => {
        Swal.close();
        this.Toast.fire({ icon: 'success', title: 'Daño eliminado con éxito' });
        this.documentUploaded.emit();
      },
      error: (err) => {
        Swal.close();
        Swal.fire('Error', err.error?.message ?? 'Error al eliminar.', 'error');
      }
    });
  }

  // Actualizar los campos del formulario reactivo del siniestro
  updateClaimFormTotals(list: any[]) {
    const form = this.claimForm();
    if (!form) return;
    const sumLabor = list.reduce((acc, d) => acc + Number(d.laborCost || 0), 0);
    const sumParts = list.reduce((acc, d) => acc + Number(d.partsCost || 0), 0);
    const sumTotal = this.isTotalLoss ? (list.length > 0 ? this.currentVehicleValue : 0) : (sumLabor + sumParts);
    form.get('laborCost')?.setValue(sumLabor, { emitEvent: false });
    form.get('partsCost')?.setValue(sumParts, { emitEvent: false });
    form.get('totalCost')?.setValue(sumTotal, { emitEvent: false });
  }

  // Actualiza el tipo de vehículo
  updateVehicleType(form: FormGroup) {
    const claimType = form.get('claimType')?.value;
    if (claimType === 'ACCIDENT_THIRD_PARTY') {
      this._updateThirdPartyVehicleType(form);
    } else {
      this._updateMemberVehicleType(form);
    }
  }

  private _updateThirdPartyVehicleType(form: FormGroup) {
    const code = form.get('thirdPartyModelCode')?.value;
    const model = this.catalogService.models().find(m => m.code === code || m.id === code);
    this.selectedVehicleType.set(model?.vehicleType ?? 'sedan');
  }

  private _updateMemberVehicleType(form: FormGroup) {
    const id = form.get('vehicleId')?.value;
    this.vehicleChange.emit(id);
    const vehicle = this.vehicleData()?.find(x => x.uuid === id || x.id === id);
    this.selectedVehicleType.set(vehicle?.modelType ?? 'sedan');
  }

  private _updateVehicleTypeFromData(data: any) {
    if (data.claimType === 'ACCIDENT_THIRD_PARTY') {
      const code = data.thirdPartyInfo?.modelCode ?? data.thirdPartyInfo?.thirdPartyModelCode;
      const model = this.catalogService.models().find(m => m.code === code || m.id === code);
      this.selectedVehicleType.set(model?.vehicleType ?? 'sedan');
    } else {
      const type = data.vehicle?.model?.vehicleType ?? data.vehicle?.modelType;
      this.selectedVehicleType.set(type ?? 'sedan');
    }
  }

  private _registerFormSubscriptions(form: FormGroup) {
    this._clearSubscriptions();
    this._subs.push(
      form.get('vehicleId')?.valueChanges.subscribe((vehicleId) => {
        this.updateVehicleType(form);
        if (vehicleId) this.loadDeductiblePercentages(vehicleId);
      }),
      form.get('date')?.valueChanges.subscribe(() => {
        const vehicleId = form.get('vehicleId')?.value;
        if (vehicleId) this.loadDeductiblePercentages(vehicleId);
      }),
      form.get('thirdPartyModelCode')?.valueChanges.subscribe(() => this.updateVehicleType(form)),
      form.get('claimType')?.valueChanges.subscribe(t => this._onClaimTypeChange(form, t))
    );
  }

  private _clearSubscriptions() {
    this._subs.forEach(s => s?.unsubscribe());
    this._subs = [];
  }

  private _onClaimTypeChange(form: FormGroup, type: string) {
    this.updateVehicleType(form);
    this.claimTypeChange.emit(type);
  }

  loadDeductiblePercentages(vehicleUuid: string) {
    if (!vehicleUuid) return;
    const form = this.claimForm();
    const partyUuid = this.partyData()?.uuid ?? '';
    const rawDate = form?.get('date')?.value;
    const dateStr = rawDate ? new Date(rawDate).toISOString().substring(0, 10) : new Date().toISOString().substring(0, 10);
    this.claimService.getDeductiblePercentages(vehicleUuid, partyUuid, dateStr).subscribe({
      next: (res) => {
        if (res) this.deductiblePercentages.set(res);
      },
      error: (err) => console.error('Error al cargar porcentajes de deducible', err)
    });
  }

  // Captura imagen del daño en el canvas 3D
  captureImage3D(): string | null {
    return this.vehicleThreeDComponent?.captureImage() ?? null;
  }

  // Función para parsear JSON
  parseJSON(val: any): string[] {
    if (Array.isArray(val)) return val;
    try {
      return JSON.parse(val || '[]');
    } catch {
      return [];
    }
  }

  formatPartName(part: string): string {
    return formatVehiclePartLabel(part, this.selectedVehicleType());
  }

  affectedPartsLabel(det: any): string {
    return formatVehiclePartList(det?.affectedParts, this.selectedVehicleType());
  }

  formatNumber(value: any): string {
    const num = Number(value ?? 0);
    return num.toLocaleString('es-CL');
  }

  approveDetail(det: any) {
    const blocked = this.approveDetailBlockedReason(det);
    if (blocked) {
      this.Toast.fire({ icon: 'warning', title: blocked });
      return;
    }
    this._updateDetailStatusServer(det, 'APPROVED');
    this.detailDocumentComp?.approveAllDetailDocuments(det);
  }

  /** Hay evidencias observadas: el revisor no puede aprobar el daño hasta el reemplazo. */
  approveDetailBlockedReason(det: any): string {
    const count = this.detailDocumentComp?.observedDocsForDetail(det)?.length ?? 0;
    if (count) {
      return `Hay ${count} archivo(s) en corrección. Espere el reemplazo antes de aprobar el daño.`;
    }
    return '';
  }

  saveNegligenceFromReviewer(isNegligence: boolean) {
    const det = this.selectedDetailView();
    if (!det?.uuid) {
      this.selectedDetailView.set({ ...det, isNegligence });
      return;
    }
    Swal.fire({
      title: isNegligence ? '¿Marcar negligencia en este daño?' : '¿Quitar negligencia de este daño?',
      text: 'Solo afecta el deducible de esta línea. El total del siniestro se recalcula sumando todas las líneas.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, guardar'
    }).then(r => {
      if (!r.isConfirmed) {
        this.selectedDetailView.set({ ...det, isNegligence: !isNegligence });
        return;
      }
      this._updateDetailOnServer({ ...det, isNegligence });
    });
  }

  rejectDetail(det: any) {
    this._updateDetailStatusServer(det, 'REJECTED');
  }

  getEntityName(code: number | string): string {
    if (!code) return this.partyData()?.entity?.name || 'N/A';
    const entity = this.catalogService.entities().find(e => e.code == code);
    if (entity) return entity.name;
    const fromParty = this.partyData()?.entity;
    if (fromParty && (fromParty.code == code || fromParty.name)) return fromParty.name || `Entidad ${code}`;
    return `Entidad ${code}`;
  }

  allDetailsFinalized(): boolean {
    return areAllDetailsFinalized(this.claimDetails());
  }
}